import request from '../../services/request'
import SignUtils from '../../utils/sign/SignUtils'
import { encode } from 'js-base64'
import { EServiceIDs } from '../../constants/documents'

interface IResponseCreateSignedPdf {
    error: string
}

const URLS_GET_PDF_FOR_SIGN = [
    '/api/extension/v1/files/pdf_account_open',
    '/api/extension/v1/files/pdf_profile_edit',
    '/api/extension/v1/files/pdf_profile_create'
]
const URL_SAVE_FILE = '/api/documents/v1/file'
const HTTP_STATUS_OK = 200

// Валидация входных параметров
const validateInputs = (
    requestId: string,
    thumbprint: string,
    serviceId: number,
    requestGetPDF: any,
): string | null => {
    if (!requestId?.trim()) {
        return 'requestId is required'
    }
    if (!thumbprint?.trim()) {
        return 'thumbprint is required'
    }
    if (!requestGetPDF?.template_name?.trim()) {
        return 'template_name is required'
    }

    if (!Object.values(EServiceIDs).includes(serviceId)) {
        return `Invalid serviceId`
    }

    return null
}

// Вспомогательная функция для получения PDF из extension
const fetchPDF = async (requestGetPDF: any, fetchURLId: number): Promise<ArrayBuffer> => {
    const fetchURL = URLS_GET_PDF_FOR_SIGN[fetchURLId]
    const { data, status } = await request.post(
        fetchURL,
        { ...requestGetPDF },
        { responseType: 'blob' },
    )

    if (status !== HTTP_STATUS_OK) {
        throw new Error('PDF file generation error')
    }

    return await blobToArrayBuffer(data)
}

// Вспомогательная функция для конвертации Blob в ArrayBuffer
const blobToArrayBuffer = (blob: Blob): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.onload = () => resolve(fileReader.result as ArrayBuffer)
        fileReader.onerror = () => reject(new Error('File reader error'))
        fileReader.readAsArrayBuffer(blob)
    })
}

// Вспомогательная функция для подписания файла ЭЦП
const createSignature = async (thumbprint: string, pdfData: ArrayBuffer): Promise<ArrayBuffer> => {
    const base64Data = encode(pdfData)
    const signedMessage = await SignUtils.DetachedSignCreate(thumbprint, base64Data)
    const signFileBlob = new Blob([signedMessage], { type: 'application/octet-stream' })
    return await blobToArrayBuffer(signFileBlob)
}

// Вспомогательная функция для сохранения файла
const saveFile = async (
    fileData: ArrayBuffer,
    fileName: string,
    serviceId: number,
): Promise<void> => {
    const { status } = await request.post(URL_SAVE_FILE, fileData, {
        headers: {
            Filename: encodeURI(fileName),
            'Service-ID': serviceId,
        },
    })

    if (status !== HTTP_STATUS_OK) {
        throw new Error(`Failed to save file: ${fileName}`)
    }
}

// Основная функция создания, подписания ЭЦП и сохранения pdf и sig файлов
export const createSignedPdf = async (
    requestId: string,
    thumbprint: string,
    serviceId: number,
    requestGetPDF: any,
    fetchURLId = 0,
): Promise<IResponseCreateSignedPdf> => {
    // Валидация
    const validationError = validateInputs(requestId, thumbprint, serviceId, requestGetPDF)
    if (validationError) {
        return { error: validationError }
    }

    try {
        // Получение PDF
        const pdfBuffer = await fetchPDF(requestGetPDF, fetchURLId)

        // Создание подписи
        const signatureBuffer = await createSignature(thumbprint, pdfBuffer)

        // Сохранение файла PDF
        const pdfFileName = `${requestId}.pdf`
        await saveFile(pdfBuffer, pdfFileName, serviceId)

        // Сохранение файла подписи
        const signatureFileName = `${pdfFileName}.sig`
        await saveFile(signatureBuffer, signatureFileName, serviceId)

        return { error: '' }
    } catch (err: any) {
        return { error: err.message || 'Unknown error' }
    }
}
